import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useProtectedAsset } from "@/hooks/useProtectedAsset";
import {
  ArrowDown,
  ArrowRight,
  Award,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Download,
  FileText,
  Loader2,
  Lock,
  LogOut,
  MonitorPlay,
  Play,
  Sparkles,
  TrendingUp,
  Upload,
} from "lucide-react";
import Aurora from "@/components/Aurora";
import Reveal from "@/components/Reveal";
import { useAuth } from "@/contexts/AuthContext";
import { COURSE } from "@/data/course";

// ---------- Helpers ----------
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768);
  }, []);
  return isMobile;
}

function moduleProgress(user) {
  if (!user?.progress) return { completed: 0, total: COURSE.modules.length + 1 };
  const allIds = [...COURSE.modules.map((m) => m.id), COURSE.bonus.id];
  const completed = allIds.filter((id) => user.progress[id]?.completed).length;
  return { completed, total: allIds.length };
}

// ---------- Framework Resource Card ----------
function ResourceDownloadButton({ resourceKey, resourceId }) {
  const { url, loading } = useProtectedAsset(resourceKey);
  return (
    <a
      href={url || undefined}
      download
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => { if (!url) e.preventDefault(); }}
      className={`shrink-0 w-10 h-10 rounded-full grid place-items-center border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition-all ${!url ? "opacity-60 cursor-wait" : ""}`}
      aria-label="Download resource"
      data-testid={`resource-download-${resourceId}`}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
    </a>
  );
}

function FrameworkDownloadButton({ frameworkKey, isMobile }) {
  const { url, loading } = useProtectedAsset(frameworkKey);
  return (
    <a
      href={url || undefined}
      {...(!isMobile ? { download: true } : {})}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={!url}
      onClick={(e) => { if (!url) e.preventDefault(); }}
      className={`inline-flex items-center justify-center gap-2 w-full px-5 py-4 rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-bold text-sm tracking-[0.1em] uppercase shadow-[0_0_32px_rgba(0,229,255,0.4)] hover:shadow-[0_0_48px_rgba(0,229,255,0.6)] transition-all ${!url ? "opacity-60 cursor-wait" : ""}`}
      data-testid="framework-download-btn"
    >
      <Download className="w-5 h-5 shrink-0" />
      <span>{loading ? "Loading Framework…" : "Download Framework"}</span>
    </a>
  );
}

function FrameworkCard({ lesson, isCompleted, onMarkComplete, isLocked }) {
  const isMobile = useIsMobile();
  const steps = [
    { label: "Download the framework" },
    { label: "Upload it into your Claude chat" },
    { label: "Complete the exercise with Claude" },
    { label: "Return here before continuing" },
  ];

  return (
    <div className="flex flex-col gap-5 w-full min-w-0" data-testid="framework-card">
      {/* Title + description */}
      <div className="min-w-0">
        <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-1 break-words">
          {lesson.section} &middot; {lesson.duration} &middot; Framework Resource
        </p>
        <h3 className="font-display text-white text-lg sm:text-2xl leading-snug break-words">{lesson.title}</h3>
        {lesson.description && (
          <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{lesson.description}</p>
        )}
      </div>

      {/* Download button — fetched through the authenticated /api/course/asset
          endpoint (private R2 bucket), not a public CDN URL. */}
      <FrameworkDownloadButton frameworkKey={lesson.key} isMobile={isMobile} />

      {/* Instruction steps */}
      <div className="glass-card p-5 sm:p-6 space-y-4">
        <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">How to use this framework</p>
        {steps.map(({ label }, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-cyan-400/10 border border-cyan-400/30 grid place-items-center shrink-0">
              <CheckCircle2 className="w-3.5 h-3.5 text-cyan-300" />
            </span>
            <span className="text-sm text-zinc-300">{label}</span>
          </div>
        ))}
      </div>

      {/* Mark complete */}
      {!isLocked && (
        <div>
          {isCompleted ? (
            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-cyan-400/30 text-cyan-300 text-xs tracking-[0.15em] uppercase">
              <CheckCircle2 className="w-4 h-4" /> Completed
            </div>
          ) : (
            <button
              onClick={onMarkComplete}
              data-testid="mark-complete-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-semibold text-xs tracking-[0.15em] uppercase shadow-[0_0_24px_rgba(0,229,255,0.3)] hover:shadow-[0_0_32px_rgba(0,229,255,0.5)] transition-all"
            >
              <CheckCircle2 className="w-4 h-4" /> Mark as Complete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Screen Panel ----------
function ScreenPanel({ lesson, isCompleted, onMarkComplete, isLocked, onPrev, onNext, hasPrev, hasNext }) {
  if (!lesson) {
    return (
      <div
        className="relative aspect-video glass-card overflow-hidden grid place-items-center mb-6"
        data-testid="screen-panel-placeholder"
      >
        <div className="text-center space-y-3 px-4">
          <MonitorPlay className="w-12 h-12 text-zinc-700 mx-auto" />
          <p className="text-zinc-500 text-sm tracking-[0.2em] uppercase">Select a lesson to begin</p>
        </div>
      </div>
    );
  }

  const { contentType, key, title, section, duration } = lesson;
  const { url, loading: assetLoading } = useProtectedAsset(contentType === "video" || contentType === "slide" ? key : null);

  // Framework resources get their own full-width card, not the video/slide panel
  if (contentType === "framework") {
    return (
      <div className="glass-card overflow-hidden mb-6 p-6 sm:p-8" data-testid="screen-panel-framework">
        <FrameworkCard
          lesson={lesson}
          isCompleted={isCompleted}
          onMarkComplete={onMarkComplete}
          isLocked={isLocked}
        />
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="screen-panel">
      <div className="glass-card overflow-hidden">
        {contentType === "video" ? (
          <div className="relative aspect-video bg-black">
            {assetLoading || !url ? (
              <div className="w-full h-full grid place-items-center">
                <Loader2 className="w-8 h-8 text-cyan-300 animate-spin" />
              </div>
            ) : (
              <video
                key={url}
                className="w-full h-full"
                controls
                controlsList="nodownload"
                preload="metadata"
                poster={lesson.poster}
                data-testid="screen-video-player"
              >
                <source src={url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        ) : contentType === "slide" ? (
          <div className="relative aspect-video bg-black/50 overflow-hidden">
            {assetLoading || !url ? (
              <div className="w-full h-full grid place-items-center">
                <Loader2 className="w-8 h-8 text-cyan-300 animate-spin" />
              </div>
            ) : (
              <img
                key={url}
                src={url}
                alt={title}
                className="w-full h-full object-contain"
                data-testid="screen-slide-image"
              />
            )}
          </div>
        ) : (
          <div className="relative aspect-video grid place-items-center">
            <div className="text-center space-y-2 px-4">
              <Sparkles className="w-8 h-8 text-zinc-600 mx-auto" />
              <p className="text-zinc-500 text-sm tracking-[0.2em] uppercase">No media attached to this lesson yet.</p>
            </div>
          </div>
        )}
      </div>

      {/* Row 1: title (left) + Prev / Next (right) */}
      <div className="mt-4 flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300">
            {section} &middot; {duration}
          </p>
          <h3 className="font-display text-white text-lg sm:text-xl mt-0.5">{title}</h3>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasPrev && (
            <button
              onClick={onPrev}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-white/15 text-zinc-400 text-xs tracking-[0.12em] uppercase hover:text-zinc-200 hover:border-white/30 transition-all"
              data-testid="prev-lesson-btn"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
          )}
          {hasNext && (
            <button
              onClick={onNext}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-cyan-400/50 text-cyan-300 text-xs tracking-[0.12em] uppercase hover:bg-cyan-400/10 hover:border-cyan-300 transition-all"
              data-testid="next-lesson-btn"
            >
              Next Lesson <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Row 2: Mark as Complete (position unchanged) */}
      {!isLocked && (
        <div className="mt-3">
          {isCompleted ? (
            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-cyan-400/30 text-cyan-300 text-xs tracking-[0.15em] uppercase">
              <CheckCircle2 className="w-4 h-4" /> Completed
            </div>
          ) : (
            <button
              onClick={onMarkComplete}
              data-testid="mark-complete-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-black font-semibold text-xs tracking-[0.15em] uppercase shadow-[0_0_24px_rgba(0,229,255,0.3)] hover:shadow-[0_0_32px_rgba(0,229,255,0.5)] transition-all"
            >
              <CheckCircle2 className="w-4 h-4" /> Mark as Complete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Lesson Row ----------
function LessonRow({ mod, globalIndex, isCompleted, isActive, isLocked, onClick }) {
  const { contentType } = mod;

  let StatusIcon = Circle;
  let iconCls = "text-zinc-600";
  if (isLocked)          { StatusIcon = Lock;         iconCls = "text-zinc-600"; }
  else if (isCompleted)  { StatusIcon = CheckCircle2; iconCls = "text-cyan-300"; }
  else if (contentType === "video")     { StatusIcon = Play;      iconCls = "text-zinc-500"; }
  else if (contentType === "slide")     { StatusIcon = MonitorPlay; iconCls = "text-zinc-500"; }
  else if (contentType === "framework") { StatusIcon = Download;  iconCls = "text-zinc-500"; }

  const typeLabel =
    contentType === "video"     ? "Video" :
    contentType === "slide"     ? "Slide" :
    contentType === "framework" ? "Framework" : "Lesson";

  return (
    <button
      onClick={onClick}
      disabled={isLocked}
      data-testid={`lesson-row-${mod.id}`}
      className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3.5 text-left transition-all border-b border-white/5 last:border-b-0 ${
        isActive
          ? "bg-cyan-400/5 border-l-2 border-l-cyan-400/60"
          : "hover:bg-white/[0.03]"
      } ${isLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <StatusIcon className={`w-4 h-4 shrink-0 ${iconCls}`} />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-[10px] tracking-[0.2em] font-mono text-zinc-600 shrink-0">
          {String(globalIndex + 1).padStart(2, "0")}
        </span>
        <span className={`text-sm font-medium truncate ${isActive ? "text-cyan-300" : "text-zinc-200"}`}>
          {mod.title}
        </span>
      </div>
      <div className="hidden sm:flex items-center gap-1.5 shrink-0 ml-2">
        <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-600">{typeLabel}</span>
        <span className="text-zinc-700">·</span>
        <span className="text-[10px] text-zinc-500">{mod.duration}</span>
      </div>
      <span className="sm:hidden text-[10px] text-zinc-600 shrink-0">{mod.duration}</span>
    </button>
  );
}

// ---------- Section Accordion ----------
function SectionAccordion({
  section, mods, globalIndexMap, isOpen, onToggle,
  activeLesson, onSelectLesson, userProgress, isLocked, isBonusSection,
}) {
  const completedCount = mods.filter((m) => userProgress?.[m.id]?.completed).length;

  return (
    <div className={`glass-card overflow-hidden mb-3 ${isBonusSection ? "border-cyan-400/20" : ""}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 sm:px-6 py-4 text-left transition-colors hover:bg-white/[0.03]"
        data-testid={`section-accordion-${section.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isOpen
            ? <ChevronDown  className="w-4 h-4 text-zinc-400 shrink-0" />
            : <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />}
          <span className={`font-display text-base sm:text-lg font-semibold truncate ${isBonusSection ? "text-cyan-300" : "text-white"}`}>
            {isBonusSection && (
              <span className="text-[10px] tracking-[0.25em] uppercase text-cyan-300 font-normal mr-2 align-middle">BONUS ·</span>
            )}
            {section}
          </span>
        </div>
        <span className="text-xs tracking-[0.2em] uppercase text-zinc-500 shrink-0 ml-4">
          {completedCount} / {mods.length}
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-white/5">
          {mods.map((m) => (
            <LessonRow
              key={m.id}
              mod={m}
              globalIndex={globalIndexMap.get(m.id) ?? 0}
              isCompleted={!!userProgress?.[m.id]?.completed}
              isActive={activeLesson?.id === m.id}
              isLocked={isLocked}
              onClick={() => !isLocked && onSelectLesson(m)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Dashboard ----------
export default function Dashboard() {
  const { user, logout, setProgress } = useAuth();
  const navigate = useNavigate();
  const [savingId, setSavingId]         = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const isPaid   = !!user?.has_paid;
  const { completed, total } = moduleProgress(user);
  const percent  = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Global index map: lesson id → 0-based index
  const globalIndexMap = useMemo(() => {
    const map = new Map();
    COURSE.modules.forEach((m, i) => map.set(m.id, i));
    map.set(COURSE.bonus.id, COURSE.modules.length);
    return map;
  }, []);

  // Group core modules by section
  const grouped = useMemo(() => {
    const map = new Map();
    COURSE.modules.forEach((m) => {
      if (!map.has(m.section)) map.set(m.section, []);
      map.get(m.section).push(m);
    });
    return [...map.entries()];
  }, []);

  // Default: first section open
  const [expandedSections, setExpandedSections] = useState(
    () => new Set(grouped[0] ? [grouped[0][0]] : [])
  );

  function toggleSection(name) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }

  // Flat ordered list for sequential prev/next navigation
  const allLessons = useMemo(() => [...COURSE.modules, COURSE.bonus], []);
  const activeIdx = activeLesson ? allLessons.findIndex((m) => m.id === activeLesson.id) : -1;
  const hasPrev = activeIdx > 0;
  const hasNext = activeIdx >= 0 && activeIdx < allLessons.length - 1;

  function onSelectLesson(mod) {
    setActiveLesson(mod);
    // Auto-expand the section this lesson belongs to
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.add(mod.section === "Bonus" ? "Bonus" : mod.section);
      return next;
    });
  }

  function onPrev() { if (hasPrev) onSelectLesson(allLessons[activeIdx - 1]); }
  function onNext() { if (hasNext) onSelectLesson(allLessons[activeIdx + 1]); }

  async function onToggleComplete(moduleId, completedFlag) {
    setSavingId(moduleId);
    try {
      await setProgress({ module_id: moduleId, completed: completedFlag });
    } catch (_) { /* swallow */ }
    finally { setSavingId(null); }
  }

  async function onMarkComplete() {
    if (!activeLesson) return;
    const already = !!user?.progress?.[activeLesson.id]?.completed;
    await onToggleComplete(activeLesson.id, !already);
  }

  function onLogout() {
    logout();
    navigate("/", { replace: true });
  }

  const activeLessonCompleted = !!user?.progress?.[activeLesson?.id]?.completed;

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <Aurora variant="default" rings />

      {/* Header */}
      <header className="relative z-40 border-b border-cyan-500/10 backdrop-blur-xl bg-black/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="dashboard-brand">
            <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shadow-[0_0_24px_rgba(0,229,255,0.5)]">
              <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
            </span>
            <span className="font-display text-white text-lg">
              <span className="font-normal">Favor</span>{" "}
              <span className="font-bold">Studio.</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400">
              {user?.is_admin && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-[10px] tracking-[0.25em] font-medium">
                  ⚡ ADMIN
                </span>
              )}
              <span className="text-cyan-300">{user?.email}</span>
            </div>
            <button
              data-testid="dashboard-logout"
              onClick={onLogout}
              className="text-xs tracking-[0.2em] uppercase text-zinc-300 hover:text-cyan-300 transition-colors inline-flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="relative max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14">

        {/* Welcome + Progress */}
        <Reveal x={-40} blur={6}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <div className="label-chip"><span className="dot" /><span>STUDENT DASHBOARD</span></div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mt-5 max-w-3xl">
                <span className="font-normal">Welcome to Your </span>
                <span className="font-bold">AI Employees Course.</span>
              </h1>
              <p className="mt-4 text-zinc-400 max-w-xl leading-relaxed">
                Work through the lessons at your own pace. Mark each one complete as you go — your progress is saved automatically.
              </p>
            </div>

            <div className="glass-card p-6 min-w-[280px]" data-testid="progress-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-cyan-300">
                  <TrendingUp className="w-4 h-4" /> Progress
                </div>
                <span className="font-display text-sm tracking-[0.05em]">
                  <span className="font-bold text-cyan-300">{completed}</span>
                  <span className="font-normal text-zinc-500"> / {total} Completed</span>
                </span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-700 ease-out"
                  style={{ width: `${percent}%`, boxShadow: "0 0 16px rgba(0,229,255,0.6)" }}
                />
              </div>
              <p className="mt-3 text-xs text-zinc-400">
                <span className="text-white font-medium">{percent}%</span> complete
              </p>
            </div>
          </div>
        </Reveal>

        {!isPaid && (
          <div className="mb-8 glass-card p-5 flex items-start gap-3 border-amber-400/40" data-testid="payment-locked-banner">
            <Lock className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
            <div>
              <p className="text-white font-medium">Course content is locked.</p>
              <p className="text-zinc-400 text-sm mt-1">
                Your account doesn&apos;t have a confirmed purchase.{" "}
                <Link to="/" className="text-cyan-300 hover:underline">Enroll for $997</Link> to unlock all lessons.
              </p>
            </div>
          </div>
        )}

        {/* Two-column: screen (left/top) + accordion (right/bottom) */}
        <div className="flex flex-col xl:flex-row gap-8 items-start">

          {/* Screen panel — sticky on xl */}
          <div className="w-full xl:w-[58%] xl:sticky xl:top-8 min-w-0">
            <ScreenPanel
              lesson={activeLesson}
              isCompleted={activeLessonCompleted}
              onMarkComplete={onMarkComplete}
              isLocked={!isPaid}
              onPrev={onPrev}
              onNext={onNext}
              hasPrev={hasPrev}
              hasNext={hasNext}
            />
          </div>

          {/* Accordion */}
          <div className="w-full xl:flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl sm:text-2xl">
                <span className="font-normal">Course </span>
                <span className="font-bold">Lessons.</span>
              </h2>
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
                {COURSE.modules.length + 1} lessons
              </span>
            </div>

            {grouped.map(([section, mods]) => (
              <SectionAccordion
                key={section}
                section={section}
                mods={mods}
                globalIndexMap={globalIndexMap}
                isOpen={expandedSections.has(section)}
                onToggle={() => toggleSection(section)}
                activeLesson={activeLesson}
                onSelectLesson={onSelectLesson}
                userProgress={user?.progress}
                isLocked={!isPaid}
                isBonusSection={false}
              />
            ))}

            {/* Bonus */}
            <SectionAccordion
              key="Bonus"
              section={COURSE.bonus.title}
              mods={[COURSE.bonus]}
              globalIndexMap={globalIndexMap}
              isOpen={expandedSections.has("Bonus")}
              onToggle={() => toggleSection("Bonus")}
              activeLesson={activeLesson}
              onSelectLesson={onSelectLesson}
              userProgress={user?.progress}
              isLocked={!isPaid}
              isBonusSection={true}
            />
          </div>
        </div>

        {/* PDF Resources */}
        <section className="mt-14 overflow-x-hidden">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl">
              <span className="font-normal">Framework </span>
              <span className="font-bold">Downloads.</span>
            </h2>
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{COURSE.resources.length} files</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {COURSE.resources.map((r) => (
              <div
                key={r.id}
                data-testid={`resource-${r.id}`}
                className={`glass-card p-5 flex items-center gap-4 overflow-hidden min-w-0 ${!isPaid ? "opacity-60" : ""}`}
              >
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/30 grid place-items-center shrink-0">
                  <FileText className="w-5 h-5 text-cyan-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{r.type}</p>
                  <p className="text-white text-sm font-medium truncate mt-0.5">{r.title}</p>
                  {r.description && (
                    <p className="text-zinc-500 text-xs mt-0.5 truncate">{r.description}</p>
                  )}
                </div>
                {isPaid ? (
                  <ResourceDownloadButton resourceKey={r.key} resourceId={r.id} />
                ) : (
                  <button
                    disabled
                    className="shrink-0 w-10 h-10 rounded-full grid place-items-center border border-white/10 text-zinc-600 cursor-not-allowed"
                    aria-label="Download resource"
                  >
                    <Lock className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Certification CTA */}
        <section className="mt-14">
          <div className="glass-card p-7 sm:p-9 relative overflow-hidden">
            <div className="aurora-blob aurora-blue" style={{ width: 420, height: 420, top: -120, left: -80, opacity: 0.4 }} />
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shrink-0 shadow-[0_0_30px_rgba(0,229,255,0.45)]">
                  <Award className="w-6 h-6 text-black" strokeWidth={2.4} />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-cyan-300">CERTIFICATION</p>
                  <h3 className="font-display text-white text-xl sm:text-2xl mt-1">
                    <span className="font-normal">Complete All Lessons to </span>
                    <span className="font-bold">Earn Your Cert.</span>
                  </h3>
                  <p className="mt-2 text-zinc-400 text-sm leading-relaxed max-w-xl">
                    Finish all {total} lessons and we&apos;ll issue your AI Agents Builder certificate from Favor Studio Agency.
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-display text-cyan-300 text-3xl"><span className="font-bold">{percent}%</span></p>
                <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">complete</p>
              </div>
            </div>
          </div>
        </section>

        {!isPaid && (
          <div className="mt-10 text-center">
            <Link to="/" className="btn-aurora inline-flex">
              UNLOCK FULL COURSE — $997 <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
